/**************************************************************************************************************************
 Copyright (c) 2023 WorkEarly Results LLP

     Name: statics.ts
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import Utils from '@/utils';
import Links from './links';
import { IconType } from 'react-icons/lib';
import { FaFacebookF, FaLinkedinIn } from 'react-icons/fa';
/**************************************************************************************************************************
 TYPES
***************************************************************************************************************************/
export type ProjectType = 'All' | 'Structural Steel' | 'Misc';

interface NavigationLink {
  id: string;
  label: string;
  href: string;
}

interface IntroBgItem {
  id: string;
  src: string;
}

interface IntroDescItem {
  id: string;
  title: string;
  desc: string;
}

interface AffiliationScrollItem {
  id: string;
  src: string;
}

interface ServiceItem {
  id: string;
  src: string;
  title: string;
  desc: string;
}

interface UniquePoint {
  id: string;
  point: string;
}

interface ProjectItem {
  id: string;
  type: ProjectType;
  label: string;
  src: string;
}

interface AddressItem {
  id: string;
  country: string;
  address: string;
}

type PhoneItem = NavigationLink & {
  country: string;
};

interface SocialItem {
  id: string;
  icon: IconType;
  href: string;
}

interface ProjectFilterItem {
  id: string;
  label: string;
  type: ProjectType;
}

export interface TimingItem {
  id: string;
  day: string;
  timing: string;
}
/**************************************************************************************************************************
 DEFINITIONS
***************************************************************************************************************************/
const NavigationLinks: NavigationLink[] = [
  {
    id: Utils.getUniqueId('navitem'),
    label: 'layout.nav-links.home',
    href: Links.Public.HOME
  },
  {
    id: Utils.getUniqueId('navitem'),
    label: 'layout.nav-links.about-us',
    href: Links.Public.ABOUT_US
  },
  {
    id: Utils.getUniqueId('navitem'),
    label: 'layout.nav-links.services',
    href: Links.Public.SERVICES
  },
  {
    id: Utils.getUniqueId('navitem'),
    label: 'layout.nav-links.projects',
    href: Links.Public.PROJECTS
  },
  {
    id: Utils.getUniqueId('navitem'),
    label: 'layout.nav-links.contact-us',
    href: Links.Public.CONTACT_US
  }
];

const IntroBgItems: IntroBgItem[] = [
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/intro/1.jpg'
  },
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/intro/2.jpg'
  },
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/intro/3.jpg'
  },
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/intro/4.jpg'
  },
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/intro/5.jpg'
  }
];

const IntroDescItems: IntroDescItem[] = [
  {
    id: Utils.getUniqueId('item'),
    title: 'intro.items.structural-steel-detailing.title',
    desc: 'intro.items.structural-steel-detailing.desc'
  },
  {
    id: Utils.getUniqueId('item'),
    title: 'intro.items.misc-steel-detailing.title',
    desc: 'intro.items.misc-steel-detailing.desc'
  },
  {
    id: Utils.getUniqueId('item'),
    title: 'intro.items.connection-design.title',
    desc: 'intro.items.connection-design.desc'
  }
];

const AffiliationScrollItems: AffiliationScrollItem[] = [
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/affiliations/1.svg'
  },
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/affiliations/4.svg'
  },
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/affiliations/2.svg'
  },
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/affiliations/3.svg'
  },
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/affiliations/5.svg'
  },
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/affiliations/6.svg'
  },
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/affiliations/7.svg'
  },
  {
    id: Utils.getUniqueId('item'),
    src: '/assets/images/affiliations/8.svg'
  }
];

const ServiceItems: ServiceItem[] = [
  {
    id: Utils.getUniqueId('service'),
    src: '/assets/images/services/1.jpg',
    title: 'services.items.steel-detailing.title',
    desc: 'services.items.steel-detailing.desc'
  },
  {
    id: Utils.getUniqueId('service'),
    src: '/assets/images/services/2.jpg',
    title: 'services.items.rebar-detailing.title',
    desc: 'services.items.rebar-detailing.desc'
  },
  {
    id: Utils.getUniqueId('service'),
    src: '/assets/images/services/3.jpg',
    title: 'services.items.material-takeoff.title',
    desc: 'services.items.material-takeoff.desc'
  },
  {
    id: Utils.getUniqueId('service'),
    src: '/assets/images/services/4.jpg',
    title: 'services.items.connection-design.title',
    desc: 'services.items.connection-design.desc'
  },
  {
    id: Utils.getUniqueId('service'),
    src: '/assets/images/services/5.jpg',
    title: 'services.items.ornamental-railing-detailing.title',
    desc: 'services.items.ornamental-railing-detailing.desc'
  },
  {
    id: Utils.getUniqueId('service'),
    src: '/assets/images/services/6.jpg',
    title: 'services.items.3d-model-rendering.title',
    desc: 'services.items.3d-model-rendering.desc'
  },
  {
    id: Utils.getUniqueId('service'),
    src: '/assets/images/services/7.jpg',
    title: 'services.items.bim-cnc.title',
    desc: 'services.items.bim-cnc.desc'
  },
  {
    id: Utils.getUniqueId('service'),
    src: '/assets/images/services/8.jpg',
    title: 'services.items.deck-services.title',
    desc: 'services.items.deck-services.desc'
  }
];

const UniquePoints: UniquePoint[] = [
  {
    id: Utils.getUniqueId('unique'),
    point: 'why-us.items.expertise'
  },
  {
    id: Utils.getUniqueId('unique'),
    point: 'why-us.items.error-free'
  },
  {
    id: Utils.getUniqueId('unique'),
    point: 'why-us.items.qualified'
  },
  {
    id: Utils.getUniqueId('unique'),
    point: 'why-us.items.research'
  },
  {
    id: Utils.getUniqueId('unique'),
    point: 'why-us.items.status-updates'
  }
];

const ProjectFilters: ProjectFilterItem[] = [
  {
    id: Utils.getUniqueId('filter'),
    label: 'projects.filters.all',
    type: 'All'
  },
  {
    id: Utils.getUniqueId('filter'),
    label: 'projects.filters.structural-steel',
    type: 'Structural Steel'
  },
  {
    id: Utils.getUniqueId('filter'),
    label: 'projects.filters.misc',
    type: 'Misc'
  }
];

const ProjectItems: ProjectItem[] = [
  {
    id: Utils.getUniqueId('project'),
    type: 'Structural Steel',
    label: 'Commercial Building',
    src: '/assets/images/projects/comm-building-1.png'
  },
  {
    id: Utils.getUniqueId('project'),
    type: 'Structural Steel',
    label: 'Commercial Building',
    src: '/assets/images/projects/comm-building-2.png'
  },
  {
    id: Utils.getUniqueId('project'),
    type: 'Structural Steel',
    label: 'Commercial Building',
    src: '/assets/images/projects/comm-building-3.png'
  },
  {
    id: Utils.getUniqueId('project'),
    type: 'Structural Steel',
    label: 'Warehouse',
    src: '/assets/images/projects/warehouse.png'
  },
  {
    id: Utils.getUniqueId('project'),
    type: 'Misc',
    label: 'Bridge',
    src: '/assets/images/projects/bridge.png'
  },
  {
    id: Utils.getUniqueId('project'),
    type: 'Misc',
    label: 'Bridge',
    src: '/assets/images/projects/bridge-1.png'
  },
  {
    id: Utils.getUniqueId('project'),
    type: 'Misc',
    label: 'Decorative Handrail',
    src: '/assets/images/projects/decorative-handrail-1.png'
  },
  {
    id: Utils.getUniqueId('project'),
    type: 'Misc',
    label: 'Industrial Building',
    src: '/assets/images/projects/indus-building-1.png'
  },
  {
    id: Utils.getUniqueId('project'),
    type: 'Misc',
    label: 'Commercial Building',
    src: '/assets/images/projects/comm-building-4.png'
  }
];

const FooterLinks: NavigationLink[] = [
  {
    id: Utils.getUniqueId('link'),
    label: 'footer.quick-links.links.about-us',
    href: Links.Public.ABOUT_US
  },
  {
    id: Utils.getUniqueId('link'),
    label: 'footer.quick-links.links.services',
    href: Links.Public.SERVICES
  },
  {
    id: Utils.getUniqueId('link'),
    label: 'footer.quick-links.links.projects',
    href: Links.Public.PROJECTS
  },
  {
    id: Utils.getUniqueId('link'),
    label: 'footer.quick-links.links.careers',
    href: Links.Public.CAREERS
  },
  {
    id: Utils.getUniqueId('link'),
    label: 'footer.quick-links.links.contact-us',
    href: Links.Public.CONTACT_US
  }
];

const AddressItems: AddressItem[] = [
  {
    id: Utils.getUniqueId('address'),
    country: 'US',
    address: '3542, D`avinci Way, Unit 3016 Melbourne, FL 32901, USA'
  },
  {
    id: Utils.getUniqueId('address'),
    country: 'India',
    address: '2nd Floor, CBM Compound, VIP Road, Visakhapatnam, Andhra Pradesh'
  },
];

const PhoneItems: PhoneItem[] = [
  {
    id: Utils.getUniqueId('address'),
    country: 'India',
    label: '+91 6300779672',
    href: 'tel:+916300779672'
  },
  {
    id: Utils.getUniqueId('address'),
    country: 'US',
    label: '+1 (954) 751-9729',
    href: 'tel:+19547519729'
  }
];

const SocialItems: SocialItem[] = [
  {
    id: Utils.getUniqueId('social'),
    icon: FaFacebookF,
    href: 'https://www.facebook.com/TEDPL/'
  },
  {
    id: Utils.getUniqueId('social'),
    icon: FaLinkedinIn,
    href: 'https://www.linkedin.com/company/tedpl/'
  }
];

const BusinessHours: TimingItem[] = [
  {
    id: Utils.getUniqueId('timing-item'),
    day: 'Monday',
    timing: '10 AM - 7 PM'
  },
  {
    id: Utils.getUniqueId('timing-item'),
    day: 'Tuesday',
    timing: '10 AM - 7 PM'
  },
  {
    id: Utils.getUniqueId('timing-item'),
    day: 'Wednesday',
    timing: '10 AM - 7 PM'
  },
  {
    id: Utils.getUniqueId('timing-item'),
    day: 'Thursday',
    timing: '10 AM - 7 PM'
  },
  {
    id: Utils.getUniqueId('timing-item'),
    day: 'Friday',
    timing: '10 AM - 7 PM'
  },
  {
    id: Utils.getUniqueId('timing-item'),
    day: 'Saturday',
    timing: 'Closed'
  },
  {
    id: Utils.getUniqueId('timing-item'),
    day: 'Sunday',
    timing: 'Closed'
  }
];

const EmailItems: NavigationLink[] = [
  {
    id: Utils.getUniqueId('email'),
    label: 'estimation@mytedc.com',
    href: 'mailto:estimation@mytedc.com'
  },
  {
    id: Utils.getUniqueId('email'),
    label: 'info@mytedc.com',
    href: 'mailto:info@mytedc.com'
  }
];

const Statics = {
  NavigationLinks,
  IntroBgItems,
  IntroDescItems,
  AffiliationScrollItems,
  ServiceItems,
  UniquePoints,
  ProjectFilters,
  ProjectItems,
  FooterLinks,
  AddressItems,
  PhoneItems,
  SocialItems,
  BusinessHours,
  EmailItems
};

export default Statics;
